import Navigation from "@/components/Navigation";
import Hero from "@/components/Hero";
import TrustIndicators from "@/components/TrustIndicators";
import BeforeAfterComparison from "@/components/BeforeAfterComparison";
import Features from "@/components/Features";
import CRMIntegrationShowcase from "@/components/CRMIntegrationShowcase";
import { Suspense, lazy } from "react";
const CRMIntegrationHub = lazy(() => import("@/components/CRMIntegrationHub"));
const CloudPartnersShowcase = lazy(() => import("@/components/CloudPartnersShowcase"));
import UseCases from "@/components/UseCases";
import InteractiveAudioPlayer from "@/components/InteractiveAudioPlayer";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import logo from "@/assets/holionix-logo.jpeg";
import { organizationSchema, productSchema } from "@/utils/structuredData";
import { track } from "@/utils/analytics";

const Index = () => {
  const comparisonItems = [
    {
      problem: "Long customer wait times (5-15 minutes average)",
      solution: "Instant response - zero wait time, 24/7 availability"
    },
    {
      problem: "High operational costs - $15-30 per agent/hour",
      solution: "40% cost reduction with unlimited concurrent conversations"
    },
    {
      problem: "Limited staffing - 24/7 coverage requires multiple shifts",
      solution: "Always available - never sick, never tired, perfectly consistent"
    },
    {
      problem: "Inconsistent quality - varies by agent training and experience",
      solution: "100% consistent excellence powered by 20+ AI frameworks"
    },
    {
      problem: "Difficult to scale - hiring and training takes weeks",
      solution: "Instant scalability - handle 1 or 1 million calls with zero degradation"
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [organizationSchema, productSchema]
        }}
      />
      <Navigation />
      <Hero />
      <TrustIndicators />
      
      {/* AI Voice Demo Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Experience Our <span className="gradient-brand-text">AI Voice Agents</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Human-like conversations with multiple accents and dialects. Click play to hear the difference.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <InteractiveAudioPlayer
              agentName="Sarah"
              agentRole="Customer Support Agent"
              avatar={logo}
              accent="American English"
              audioType="inbound-support"
            />
            <InteractiveAudioPlayer
              agentName="Raj"
              agentRole="Sales Agent"
              avatar={logo}
              accent="Indian English"
              audioType="outbound-sales"
            />
            <InteractiveAudioPlayer
              agentName="Emma"
              agentRole="Outbound Marketing"
              avatar={logo}
              accent="British English"
              audioType="marketing"
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/contact">
              <Button size="lg" className="gradient-brand text-white hover:opacity-90" onClick={() => track({ name: 'cta_request_custom_voice_sample' })}>
                Request Custom Voice Sample
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Before/After Comparison */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              From Painful to <span className="gradient-brand-text">Powerful</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              See how Holionix transforms traditional customer service challenges into competitive advantages.
            </p>
          </motion.div>

          <BeforeAfterComparison items={comparisonItems} />
        </div>
      </section>

      <Features />
      <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading integration hub…</div>}>
        <CRMIntegrationHub />
      </Suspense>
      <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading partners…</div>}>
        <CloudPartnersShowcase />
      </Suspense>
      <UseCases />
      
      {/* Final CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 gradient-brand opacity-10" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Customer Interactions?
            </h2>
            <p className="text-xl text-muted-foreground mb-10">
              Join leading enterprises already using Holionix to reduce costs, improve quality, and scale infinitely.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="gradient-brand text-white hover:opacity-90 text-lg px-10 py-7" onClick={() => track({ name: 'cta_book_live_demo' })}>
                  Book Live Demo
                </Button>
              </Link>
              <Link to="/solutions">
                <Button size="lg" variant="outline" className="text-lg px-10 py-7" onClick={() => track({ name: 'cta_explore_use_cases' })}>
                  Explore Use Cases
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default Index;
